import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTable, { Column, DerivedDataObject, Instance, Resize } from 'react-table-6';
import { markCallFolderAsRead } from '../api/history-control';
import { HasClassName } from '../component-utils';
import { formatDuration } from '../date-utils';
import { useSelector } from '../hook-utils';
import { showConfirmWindow } from '../legacy';
import { Call, CallFolder, DeviceType, ExtensionStatus } from '../model';
import { actions } from '../store';
import { selectCalls, selectMissedCallsCount } from '../store/reducers/calls';
import { selectDateFormating, selectMyAccess, selectMyself } from '../store/reducers/config';
import { ColumnWidths, getGridSortOrder, selectGridColumnWidths } from '../store/reducers/localSettings';
import { selectExtension } from '../store/reducers/queueAgents';
import { TableRow } from '../table-utils';
import { formatRelativeDateTime } from './AgentsTable';
import './CallsPage.scss';
import { useCompactView } from './compactView';
import { ReactComponent as DeleteIcon } from './icons/delete.svg';
import { ReactComponent as CallHistoryDesk } from './icons/dial-desk.svg';
import { ReactComponent as CallHistoryHome } from './icons/dial-home.svg';
import { ReactComponent as CallHistoryMobile } from './icons/dial-mobile.svg';
import { ReactComponent as CallHistoryOther } from './icons/dial-other.svg';
import { ReactComponent as CallHistorySoftphone } from './icons/dial-softphone.svg';
import { ReactComponent as CallHistoryEmail } from './icons/email.svg';
import { ReactComponent as MediaCallBackIcon } from './icons/media/callback.svg';
import { ReactComponent as FacebookIcon } from './icons/media/facebook.svg';
import { ReactComponent as OutBoundIcon } from './icons/media/outbound.svg';
import { ReactComponent as TweetIcon } from './icons/media/tweet.svg';
import { ReactComponent as WebChatIcon } from './icons/media/web-chat.svg';
import { ReactComponent as SearchIcon } from './icons/toolbar-search.svg';
import Input from './Input';
import { Menu, MenuDivider, MenuItem, MenuTarget, targetFromEvent } from './Menu';
import MoreButton from './MoreButton';
import { ItemSelected, RenderSelection } from './SearchPage';
import SidebarView from './SidebarView';


interface Props extends HasClassName {
  history: Call
}

const FolderNames: CallFolder[] = ['missed', 'received', 'placed']

function getColumns(folder: CallFolder, onMore: (state: MenuState) => void, columnsWidths?: ColumnWidths): Column<Call>[] {
  let cols: Column<Call>[] = [{
    id: 'icon',
    Header: '',
    width: 42,
    resizable : false,
    accessor: c => <CallHistoryIcon history={c} />,
  }, {
    id: 'name',
    Header: folder === 'placed' ? 'To' : 'From',
    accessor: c => callHistortyName(c),
  }]

  if (folder === 'received') {
    cols.push({
      id: 'Queue',
      Header: 'Queue',
      accessor: c => c.queue,
    })
  }

  cols.push({
    id: 'subject',
    Header: 'Subject',
    accessor: c => c.subject,
  })

  if (folder !== 'missed') {
    cols.push({
      id: 'duration',
      Header: 'Duration',
      accessor: c => c.duration,
      Cell: cell => formatDuration(cell.value),
      className: 'numeric',
      headerClassName: 'numeric',
      width: 100,
    })
  }

  cols = [
    ...cols,
    {
      id: 'date',
      Header: 'Date',
      accessor: c => c.date,
      Cell: ({ original: r }) => <RelativeLongFormatDateTime date={r.date} />,
      className: 'numeric',
      headerClassName: 'numeric',
    }, {
      id: 'menuButton',
      Cell: ({ original: call }: TableRow<Call>) => <div className='actions'>
        <MoreButton className='menu-button' onClick={e => {
          e.stopPropagation()
          onMore({ target: targetFromEvent(e), item: call })
        }
        } />
      </div>,
      width: 40,
      sortable: false,
      resizable: false,
    },
  ]

  return cols.map(x => {
    if (x.id && columnsWidths && columnsWidths[x.id]) {
      return {
        ...x,
        width: columnsWidths[x.id],
      }
    }
    return x
  })
}

interface MenuState {
  target: MenuTarget
  item: Call
}

export default function CallsPage() {
  console.log('Render CallsPage')

  const dispatch = useDispatch()
  const [folder, setFolder] = useState(FolderNames[0])
  const data = useSelector(state => selectCalls(state, folder))
  const [menu, setMenu] = useState<MenuState>()
  const [selected, setSelected] = useState<string>()
  const [selection, setSelection] = useState<ItemSelected>()
  const unread = useSelector(selectMissedCallsCount) || 0
  const compactView = useCompactView()
  const [filter, setFilter] = useState('')
  const [filterKeyPress, setFilterKeyPress] = useState('')
  const selectGridItem = useSelectGridItem()
  const gridType = 'recent'
  const sorted = useSelector(s => getGridSortOrder(s, gridType))
  const widths = useSelector(s => selectGridColumnWidths(s, gridType))
  const refColumnResizeState = useRef<ColumnWidths>()
  const reactTable = useRef<ReactTable<Call>>(null);


  useEffect(() => {
    return () => {
      setSelected(undefined)
      setSelection(undefined)
      dispatch(actions.clearSelectedNumber())
    }
  }, [dispatch])

  useEffect(() => {
    if (!data) {
      dispatch(actions.fetchCalls.request(folder))
    }
  }, [dispatch, folder, data])


  function onKeyDown(e: KeyboardEvent) {
    if (e.target instanceof HTMLElement) {
      const element = e.target as HTMLElement
      if (element.id.indexOf("mce") === 0) {
        return
      }
    }
    if (data == null || data.length === 0 || reactTable.current === null) return

    const table = reactTable.current as Instance
    const sorted: Call[] = (table.getSortedData(table.getResolvedState()).sortedData as DerivedDataObject[]).map(x => x._original)

    function selectFirstRow() {
      if (!selected && sorted.length > -1) {
        const first = sorted[0]
        selectGridItem(first, folder, setSelected, setSelection)
        return true
      }
      return false
    }

    function selectNextUp() {
      const index = sorted.findIndex(d => selected !== null && selected === d.id)
      return index - 1
    }

    function selectNextDown() {
      const index = sorted.findIndex(d => selected !== null && selected === d.id)
      return index + 1
    }

    switch (e.keyCode) {
      case 38://up
        e.preventDefault()

        if (selectFirstRow()) return

        const prevIndex = selectNextUp()
        if (prevIndex < 0) return
        const prev = sorted[prevIndex]

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {
          //

        } else {

          selectGridItem(prev, folder, setSelected, setSelection)
        }
        break
      case 40://down
        e.preventDefault()

        if (selectFirstRow()) return

        const nextIndex = selectNextDown()
        if (nextIndex >= sorted.length) return
        const next = sorted[nextIndex]

        if (e.shiftKey) {
          //
        } else if (e.ctrlKey) {
          //
        } else {
          selectGridItem(next, folder, setSelected, setSelection)
        }

        break
    }
  }

  const onKeyDownRef = useRef(onKeyDown)

  useEffect(() => {
    onKeyDownRef.current = onKeyDown
  })

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      onKeyDownRef.current(e)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])


  const onColumnResize = useCallback((newResized: Resize[], event: any) => {
    newResized.forEach(element => {
      if (refColumnResizeState.current) {
        refColumnResizeState.current = { ...refColumnResizeState.current, [element.id]: element.value }
      } else {
        refColumnResizeState.current = { [element.id]: element.value }
      }
    });
  }, [])

  useEffect(() => {
    // need to send this a destruction of queue page to save widths for next time
    return () => {
      if (refColumnResizeState.current) {
        dispatch(actions.requestSetGridColumnWidth(gridType, refColumnResizeState.current))
      }
      refColumnResizeState.current = undefined;
    }

  }, [dispatch])

  const f = filterKeyPress.toLowerCase()
  const table = useMemo(() => <ReactTable ref={reactTable} className={classNames(compactView && 'compact-view')}
    data={f !== '' && data ? data.filter(c => {
      return c.from.name.toLowerCase().indexOf(f) > -1 ||
        c.from.id.toLowerCase().indexOf(f) > -1 ||
        c.subject.toLowerCase().indexOf(f) > -1
    }) : data
    }
    columns={getColumns(folder, setMenu, widths)} showPagination={false} defaultPageSize={9999999}
    getTrProps={(_: any, row?: TableRow<Call>) => row && ({
      className: (!row.original.read ? 'unread' : '') + ((menu && row.original.id === menu.item.id) || (selected && selected === row.original.id) ? ' selected' : ' '),
      onClick() {
        selectGridItem(row.original, folder, setSelected, setSelection)
      }, onContextMenu(e: React.MouseEvent) {
        setMenu({ target: targetFromEvent(e), item: row.original })
      },
    })}
    sorted={sorted}
    onSortedChange={(newSort, column) => {
      dispatch(actions.requestSetGridSortOrder(gridType, [newSort[0]]))
    }}
    minRows={0}
    NoDataComponent={() => null}
    onResizedChange={onColumnResize} />
    , [compactView, f, data, folder, widths, sorted, onColumnResize, menu, selected, selectGridItem, dispatch])

  function onClearAll() {
    showConfirmWindow('IPFX Contact Centre Client', 'Are you sure you want to clear the recent folder \'' + getFolderName(folder) + '\'?', (action: string) => {
      if (action === 'yes') {
        dispatch(actions.clearCalls(folder))
      }
    })
  }

  useEffect(() => {
    return () => {
      if (folder === 'missed') {
        markCallFolderAsRead(folder)
      }
    }
  }, [folder])

  return <SidebarView value={folder} onChange={(folder) => {
    setFolder(folder)
    setFilter('')
    setFilterKeyPress('')
    setSelected(undefined)
    setSelection(undefined)
  }} className='CallsPage'>
    <SidebarView.Sidebar>
      <SidebarView.Header>
        Recent
      </SidebarView.Header>
      {FolderNames.map(f => {
        const showUnread = f === 'missed' && unread > 0
        return <SidebarView.Item key={f} value={f} className={classNames(showUnread && 'unread')}>
          {getFolderName(f)}
          {showUnread && ` (${unread})`}
        </SidebarView.Item>
      })}
    </SidebarView.Sidebar>
    <SidebarView.Body>
      <SidebarView.Header>
        <div>{getFolderName(folder)}</div>
        <div className='searchBox'>
          <Input placeholder='From / subject' className='search' value={filter} onChange={e => setFilter(e.currentTarget.value)} autocomplete={false}
            onKeyUp={e => {
              if (e.nativeEvent.code === 'Enter' || e.nativeEvent.code === 'NumpadEnter') {
                e.stopPropagation()
                e.preventDefault()

                setFilterKeyPress(filter)

                setSelected(undefined)
                setSelection(undefined)
                dispatch(actions.clearSelectedNumber())

              } else if (e.nativeEvent.code === 'Escape') {
                setFilter('')
                setFilterKeyPress('')
              }

            }} />
          <SearchIcon className='searchIcon' />
        </div>
        <button className='clearAll' onClick={onClearAll}>
          <DeleteIcon />
          Clear all
        </button>
      </SidebarView.Header>
      <div className='body'>
        {table}
        {selection && <RenderSelection selection={selection} onClose={() => setSelection(undefined)} />}
      </div>
    </SidebarView.Body>
    {
      menu && <ContextMenu state={menu} onClose={() => setMenu(undefined)} onView={(item) => {
        selectGridItem(item, folder, setSelected, setSelection)
      }} />
    }

  </SidebarView >
}

function useSelectGridItem() {
  const dispatch = useDispatch()

  return useCallback((item: Call, folder: CallFolder, setSelected: (state: string) => void, setSelection: (state: ItemSelected) => void) => {
    if (item.mediaType === 'CALL' || item.mediaType === 'VM') {
      dispatch(actions.setSelectedNumber(item.from.id))
    } else {
      dispatch(actions.clearSelectedNumber())
    }

    setSelected(item.id)

    const id = item.mediaType === 'CALL' || item.mediaType === 'VM' ? item.callKey : item.mediaItemId
    setSelection({ id: id, type: item.mediaType, isOutgoing: (folder === 'placed') })

  }, [dispatch])
}

function CallHistoryIcon({ className, history }: Props) {
  const compact = useCompactView()
  return getIcon(history, classNames('grid-icon', className, compact && 'compact-view'))
}


function callHistortyName(history: Call) {
  if (history.mediaType === 'CALL' || history.mediaType === 'VM') {
    return history.from.name ? history.from.name + (history.from.id !== '' ? ' (' + history.from.id + ')' : '') : history.from.id
  } else if (history.mediaType === 'EMAIL') {
    return history.from.name
  } else if (history.mediaType === 'FB') {
    return history.from.name
  } else if (history.mediaType === 'TC') {
    return history.from.name
  } else if (history.mediaType === 'TWEET') {
    return history.from.name
  } else if (history.mediaType === 'OUT') {
    return history.from.name
  }
}

function getIcon(history: Call, className: string) {
  if (history.mediaType === 'EMAIL') {
    return <div title='Email' className={className}><CallHistoryEmail /></div>
  } else if (history.mediaType === 'FB') {
    return <div title='Facebook' className={className}><FacebookIcon /></div>
  } else if (history.mediaType === 'TC') {
    return <div title='Web Chat' className={className}><WebChatIcon /></div>
  } else if (history.mediaType === 'TWEET') {
    return <div title='Tweet' className={className}><TweetIcon /></div>
  } else if (history.mediaType === 'OUT') {
    return <div title='Out bound' className={className}><OutBoundIcon /></div>
  } else if (history.mediaType === 'VM') {
    return <div title='Callback' className={className}><MediaCallBackIcon /></div>
  }
  else {
    switch (history.deviceType) {
      case DeviceType.Home:
        return <div title='Home' className={className}><CallHistoryHome /></div>
      case DeviceType.Mobile:
        return <div title='Mobile' className={className}><CallHistoryMobile /></div>
      case DeviceType.Other:
        return <div title='Other' className={className}><CallHistoryOther /></div>
      case DeviceType.Softphone:
        return <div title='Softphone' className={className}><CallHistorySoftphone /></div>
      default:
        return <div title='Desk' className={className}><CallHistoryDesk /></div>
    }
  }
}

function ContextMenu({ state: { item: call, target }, onClose, onView }: { state: MenuState, onClose(): void, onView(call: Call): void }) {
  const dispatch = useDispatch()
  const myId = useSelector(selectMyself);
  const myself = useSelector(s => myId ? selectExtension(s, myId) : undefined)
  const access = useSelector(selectMyAccess)

  if (!myId || !myself || !access) return null

  const enableDial = (call.mediaType === "CALL" || call.mediaType === "VM") && (myself.status === ExtensionStatus.None || myself.status === ExtensionStatus.Released)
  //const enableEmail = call.mediaType !== 'CALL' && call.mediaType !== 'VM'

  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    {access.features.phoneDial && <MenuItem onClick={() => { if (enableDial) dispatch(actions.ccDial(call.from.id)) }} disabled={!enableDial}>
      <CallHistoryDesk />
      Dial
    </MenuItem>}
    {access.features.phoneDial && <MenuDivider />}
    <MenuItem onClick={() => onView(call)}>
      View
    </MenuItem>
  </Menu>
}

function getFolderName(f: CallFolder) {
  return f[0].toUpperCase() + f.substr(1)
}

export function RelativeLongFormatDateTime({ date }: { date: number }) {
  const format = useSelector(selectDateFormating)
  if (!format) return null

  return <>{formatRelativeDateTime(date, format.longDateFormat, format.shortTimeFormat)}</>
}

export function RelativeShortFormatDateTime({ date }: { date: number }) {
  const format = useSelector(selectDateFormating)
  if (!format) return null

  return <>{formatRelativeDateTime(date, format.shortDateFormat, format.shortTimeFormat)}</>
}

export function RelativeShortFormatDate({ date }: { date: number }) {
  const format = useSelector(selectDateFormating)
  if (!format) return null

  return <>{formatRelativeDateTime(date, format.shortDateFormat)}</>
}

/*
type FancyButtonProps<D> = TableProps<D> & {
  color: string
}

export function MyFancyButton<T>({ color, getTrProps, ...props }: FancyButtonProps<T> ) {
  function myGetTrProps() {

  }

  return <ReactTable {...props} getTrProps={} />
}
*/
